import React, { useState, useEffect, useContext } from 'react';
import request from '@nineeng/request';
import * as authentication from '../util/authentication';
import { PTWContext } from '../sections/context/PTWContext';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const SubcontractedEvents = ({
	values,
	eventIdField = 'eventId', // Field in values to get the event ID
	data, // Provided data - if set, bypasses API call and uses this data instead
}) => {
	const { isPrinting } = useContext(PTWContext);
	const [events, setEvents] = useState([]);
	const [loading, setLoading] = useState(false);
	
	// Hardcoded endpoint path - can be made configurable if needed
	const endpointPath = '/events/{eventId}/subcontractors';

	useEffect(() => {		
		if (!isPrinting) {
			setEvents([]);
			return; // Only fetch when in print view
		}

		// If data is provided (from parent, form values, or for testing), use it directly
		if (data) {
			setEvents(Array.isArray(data) ? data : []);
			return;
		}

		const fetchEvents = async () => {
			setLoading(true);
			try {
				// Try to get event ID from various possible locations in values
				const eventId = values?.[eventIdField] || values?.id || values?.event?.id;
				
				if (!eventId) {
					console.warn('[SubcontractedEvents] No eventId found in values');
					setEvents([]);
					return;
				}

				// Get endpoint prefix from values, fallback to default
				const endpointPrefix = values?.endpointPrefix || values?.apiPrefix || '/api';

				const authToken = authentication.extractAuthorization();
				const authParams = authentication.extractAuthParams();
				
				// Build the full URL: prefix + path with eventId
				const url = `${endpointPrefix}${endpointPath.replace('{eventId}', eventId)}`;
				const fullUrl = `${url}?${authParams}`;
				
				const { body } = await request.get(
					fullUrl,
					{ headers: { Authorization: authToken } },
				);
				
				const eventsData = Array.isArray(body) ? body : (body?.data || []);
				setEvents(eventsData);
			} catch (error) {
				console.error('[SubcontractedEvents] Error fetching subcontracted events:', error);
				setEvents([]);
			} finally {
				setLoading(false);
			}
		};

		fetchEvents();
	}, [isPrinting, values, eventIdField, data]);

	if (!isPrinting) return null; // Don't render when not in printing view

	if (loading) {
		return (
			<Box sx={{ padding: 2 }}>
				<Typography>Loading subcontractor list...</Typography>
			</Box>
		);
	}

	if (events.length === 0) {
		return (
			<Box sx={{ padding: 2 }}>
				<Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
					No subcontractor data available
				</Typography>
			</Box>
		);
	}

	// Split companies into two columns
	const midPoint = Math.ceil(events.length / 2);
	const leftColumn = events.slice(0, midPoint);
	const rightColumn = events.slice(midPoint);

	const renderCompany = (company, companyIndex) => {
		const companyName = company.name || company.companyName || '-';
		const users = company.users || [];
		
		return (
			<Box key={company.id || `company-${companyIndex}`} sx={{ marginBottom: 2 }}>
				<Typography 
					variant="body2" 
					sx={{ 
						fontWeight: 'bold', 
						marginBottom: 0.5,
						wordWrap: 'break-word',
						overflowWrap: 'break-word',
					}}
				>
					{companyName}
				</Typography>
				{users.length > 0 && (
					<Box sx={{ paddingLeft: 1 }}>
						{users.map((user, userIndex) => {
							const userName = typeof user === 'string' 
								? user 
								: (user.name || user.fullName || user);
							
							return (
								<Box 
									key={userIndex} 
									sx={{ 
										display: 'flex',
										alignItems: 'flex-start',
										marginBottom: 0.25,
									}}
								>
									<Typography 
										variant="body2" 
										sx={{ 
											fontSize: '0.875rem',
											wordWrap: 'break-word',
											overflowWrap: 'break-word',
											hyphens: 'auto',
											flex: 1,
											minWidth: 0, // Allows flex item to shrink below content size
										}}
									>
										{userName}
									</Typography>
									{/* Small signature space next to the name */}
									<Box sx={{ 
										minWidth: 80,
										marginLeft: 1,
										height: '1.2em', // Match line height of text
									}} />
								</Box>
							);
						})}
					</Box>
				)}
			</Box>
		);
	};

	return (
		<Box sx={{ marginTop: 1 }}>
			<Box sx={{ display: 'flex', gap: 3 }}>
				<Box sx={{ flex: 1 }}>
					{leftColumn.map((company, index) => renderCompany(company, index))}
				</Box>
				<Box sx={{ flex: 1 }}>
					{rightColumn.map((company, index) => renderCompany(company, midPoint + index))}
				</Box>
			</Box>
		</Box>
	);
};

SubcontractedEvents.type = 'SubcontractedEvents';

export default SubcontractedEvents;

